import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Check, X, AlertTriangle, ShoppingCart, FileText, Clock, CheckCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';

const PurchaseRequisitions = ({ user }) => {
  const [materialsToReorder, setMaterialsToReorder] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [prNotes, setPrNotes] = useState('');
  const [activeTab, setActiveTab] = useState('create');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [supplier, setSupplier] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allReqPage, setAllReqPage] = useState(1);

  const getVisibleMaterials = () => {
    if (!searchQuery) return selectedMaterials;
    const q = searchQuery.toLowerCase();
    return selectedMaterials.filter(mat =>
      (mat.material_code || '').toLowerCase().includes(q) ||
      (mat.material_name || '').toLowerCase().includes(q)
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [materialsRes, requisitionsRes] = await Promise.all([
        axios.get(`${API}/purchase-requisitions/materials-to-reorder`),
        axios.get(`${API}/purchase-requisitions`)
      ]);
      setMaterialsToReorder(materialsRes.data);
      setRequisitions(requisitionsRes.data);

      // Pre-select all materials for new PR
      setSelectedMaterials(materialsRes.data.map(m => ({
        ...m,
        requested_quantity: m.suggested_quantity,
        selected: true
      })));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const toggleMaterial = (materialId) => {
    setSelectedMaterials(prev =>
      prev.map(m => m.material_id === materialId ? { ...m, selected: !m.selected } : m)
    );
  };

  const toggleSelectAll = () => {
    const visible = getVisibleMaterials();
    const visibleIds = visible.map(m => m.material_id);
    const allVisibleSelected = visible.length > 0 && visible.every(m => m.selected);

    setSelectedMaterials(prev =>
      prev.map(m => visibleIds.includes(m.material_id) ? { ...m, selected: !allVisibleSelected } : m)
    );
  };

  const updateQuantity = (materialId, quantity) => {
    setSelectedMaterials(prev =>
      prev.map(m => m.material_id === materialId ? { ...m, requested_quantity: parseFloat(quantity) || 0 } : m)
    );
  };

  const createRequisition = async () => {
    const items = selectedMaterials.filter(m => m.selected && m.requested_quantity > 0);
    if (items.length === 0) {
      toast.error('Please select at least one material');
      return;
    }

    try {
      const res = await axios.post(`${API}/purchase-requisitions?notes=${encodeURIComponent(prNotes)}`, items);
      toast.success(`Purchase requisition ${res.data?.pr_number || ''} created`);
      setCreateDialogOpen(false);
      setPrNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create requisition');
    }
  };

  const handleApprove = async (prId) => {
    try {
      await axios.put(`${API}/purchase-requisitions/${prId}/approve`);
      toast.success('Requisition approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (prId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      await axios.put(`${API}/purchase-requisitions/${prId}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('Requisition rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const openConvertDialog = (pr) => {
    setSelectedPR(pr);
    setSupplier('');
    setConvertDialogOpen(true);
  };

  const convertToPO = async () => {
    try {
      const res = await axios.post(`${API}/purchase-requisitions/${selectedPR.id}/convert-to-po?supplier=${encodeURIComponent(supplier)}`);
      toast.success(`Purchase Order ${res.data.po_number} created`);
      setConvertDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to PO');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'converted_to_po': return 'bg-blue-100 text-blue-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_approval': return <Clock size={14} />;
      case 'approved': return <CheckCircle size={14} />;
      case 'rejected': return <X size={14} />;
      case 'converted_to_po': return <ShoppingCart size={14} />;
      default: return <FileText size={14} />;
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="purchase-requisitions-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Purchase Requisitions
          </h1>
          <p className="text-sm text-[hsl(145,20%,40%)] mt-2">
            Manage material reorder requests and approvals
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-stone-100 p-1">
          <TabsTrigger value="create" className="data-[state=active]:bg-white">
            <Plus size={14} className="mr-2" /> Create Requisition
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">
            <Clock size={14} className="mr-2" /> Pending Approval
            {requisitions.filter(r => r.status === 'pending_approval').length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                {requisitions.filter(r => r.status === 'pending_approval').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white">
            <CheckCircle size={14} className="mr-2" /> Approved
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-white">
            <FileText size={14} className="mr-2" /> All Requisitions
          </TabsTrigger>
        </TabsList>

        {/* Create Requisition Tab */}
        <TabsContent value="create">
          <div className="bg-white rounded-sm border border-stone-200 shadow-sm p-6">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-[hsl(145,60%,10%)]">Materials Below Reorder Level</h2>
                {materialsToReorder.length > 0 && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    <AlertTriangle size={12} /> {materialsToReorder.length} items need reorder
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={toggleSelectAll}
                  className="text-sm"
                  data-testid="select-all-btn"
                >
                  {(() => {
                    const visible = getVisibleMaterials();
                    const allVisibleSelected = visible.length > 0 && visible.every(m => m.selected);
                    return allVisibleSelected ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  disabled={selectedMaterials.filter(m => m.selected).length === 0}
                  className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
                  data-testid="create-pr-btn"
                >
                  <Plus size={16} className="mr-2" /> Create Requisition
                </Button>
              </div>
            </div>

            {materialsToReorder.length > 0 && (
              <div className="relative mb-4">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  placeholder="Search by code or material name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-9 max-w-sm"
                  data-testid="search-materials"
                />
              </div>
            )}

            {materialsToReorder.length === 0 ? (
              <div className="text-center py-12 text-[hsl(145,20%,40%)]">
                <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
                <p>All materials are above reorder level</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const visible = getVisibleMaterials();
                            return visible.length > 0 && visible.every(m => m.selected);
                          })()}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 rounded border-stone-300"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Material</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Current Stock</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Reorder Level</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">MOQ</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Suggested Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Request Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {(() => {
                      const visible = getVisibleMaterials();
                      return visible.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((mat) => (
                        <tr key={mat.material_id} className={`hover:bg-stone-50/50 ${mat.selected ? 'bg-green-50/30' : ''}`}>
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={mat.selected}
                              onChange={() => toggleMaterial(mat.material_id)}
                              className="h-4 w-4 rounded border-stone-300"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm font-medium">{mat.material_code}</td>
                          <td className="px-4 py-2 text-sm">{mat.material_name}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className="text-red-600 font-medium">{mat.current_stock}</span>
                            <span className="text-stone-400 ml-1">{mat.unit}</span>
                          </td>
                          <td className="px-4 py-2 text-sm text-stone-500">{mat.reorder_level} {mat.unit}</td>
                          <td className="px-4 py-2 text-sm text-stone-500">{mat.moq} {mat.unit}</td>
                          <td className="px-4 py-2 text-sm text-blue-600 font-medium">{mat.suggested_quantity} {mat.unit}</td>
                          <td className="px-4 py-2">
                            <Input
                              type="number"
                              value={mat.requested_quantity}
                              onChange={(e) => updateQuantity(mat.material_id, e.target.value)}
                              className="w-24 h-8"
                              min={mat.moq}
                              disabled={!mat.selected}
                            />
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                <Pagination currentPage={currentPage} totalItems={getVisibleMaterials().length} onPageChange={setCurrentPage} />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Pending Approval Tab */}
        <TabsContent value="pending">
          <div className="space-y-4">
            {requisitions.filter(r => r.status === 'pending_approval').length === 0 ? (
              <div className="bg-white rounded-sm border border-stone-200 shadow-sm p-12 text-center text-[hsl(145,20%,40%)]">
                No requisitions pending approval
              </div>
            ) : (
              requisitions.filter(r => r.status === 'pending_approval').map(pr => (
                <div key={pr.id} className="bg-white rounded-sm border border-stone-200 shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-[hsl(145,60%,10%)]">{pr.pr_number}</h3>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pr.status)}`}>
                          {getStatusIcon(pr.status)} {pr.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-[hsl(145,20%,40%)]">
                        Created by: {pr.created_by_name || pr.created_by} | {new Date(pr.created_at).toLocaleString()}
                      </p>
                      {pr.notes && <p className="text-xs text-stone-500 mt-1">Notes: {pr.notes}</p>}
                    </div>
                    {user?.role === 'admin' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleReject(pr.id)} className="text-red-600 border-red-200">
                          <X size={14} className="mr-1" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(pr.id)} className="bg-green-600 hover:bg-green-700 text-white">
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Material</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Current Stock</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Reorder Level</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Requested Qty</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {pr.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 font-medium">{item.material_name}</td>
                            <td className="px-3 py-2 text-red-600">{item.current_stock} {item.unit}</td>
                            <td className="px-3 py-2 text-stone-500">{item.reorder_level} {item.unit}</td>
                            <td className="px-3 py-2 font-medium text-blue-600">{item.requested_quantity} {item.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved">
          <div className="space-y-4">
            {requisitions.filter(r => r.status === 'approved').length === 0 ? (
              <div className="bg-white rounded-sm border border-stone-200 shadow-sm p-12 text-center text-[hsl(145,20%,40%)]">
                No approved requisitions waiting for PO conversion
              </div>
            ) : (
              requisitions.filter(r => r.status === 'approved').map(pr => (
                <div key={pr.id} className="bg-white rounded-sm border border-stone-200 shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-[hsl(145,60%,10%)]">{pr.pr_number}</h3>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pr.status)}`}>
                          {getStatusIcon(pr.status)} APPROVED
                        </span>
                      </div>
                      <p className="text-sm text-[hsl(145,20%,40%)]">
                        Approved by: {pr.approved_by_name || pr.approved_by} | {pr.approved_at && new Date(pr.approved_at).toLocaleString()}
                      </p>
                    </div>
                    <Button onClick={() => openConvertDialog(pr)} className="bg-blue-600 hover:bg-blue-700 text-white">
                      <ShoppingCart size={14} className="mr-1" /> Convert to PO
                    </Button>
                  </div>

                  <div className="text-sm text-stone-600">
                    {pr.items.length} items | Total requested: {pr.items.reduce((sum, i) => sum + i.requested_quantity, 0)} units
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* All Requisitions Tab */}
        <TabsContent value="all">
          <div className="bg-white rounded-sm border border-stone-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">PR Number</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Items</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-stone-600 uppercase">PO Number</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {requisitions.slice((allReqPage - 1) * ITEMS_PER_PAGE, allReqPage * ITEMS_PER_PAGE).map(pr => (
                  <tr key={pr.id} className="hover:bg-stone-50/50">
                    <td className="px-4 py-3 text-sm font-medium">{pr.pr_number}</td>
                    <td className="px-4 py-3 text-sm text-stone-500">{pr.created_by_name || pr.created_by}</td>
                    <td className="px-4 py-3 text-sm text-stone-500">{new Date(pr.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-stone-500">{pr.items.length} items</td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium w-fit ${getStatusColor(pr.status)}`}>
                        {getStatusIcon(pr.status)} {pr.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600">{pr.po_number || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination currentPage={allReqPage} totalItems={requisitions.length} onPageChange={setAllReqPage} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Requisition Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Purchase Requisition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-sm">
              <p className="text-sm font-medium text-[hsl(145,60%,10%)]">
                {selectedMaterials.filter(m => m.selected).length} materials selected
              </p>
              <p className="text-xs text-[hsl(145,20%,40%)]">
                Total quantity: {selectedMaterials.filter(m => m.selected).reduce((sum, m) => sum + m.requested_quantity, 0)} units
              </p>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={prNotes}
                onChange={(e) => setPrNotes(e.target.value)}
                placeholder="Add notes for the approver..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={createRequisition} className="bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white">
                Submit for Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to PO Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convert to Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-sm">
              <p className="text-sm font-medium text-[hsl(145,60%,10%)]">
                {selectedPR?.pr_number}
              </p>
              <p className="text-xs text-[hsl(145,20%,40%)]">
                {selectedPR?.items.length} items | Total: {selectedPR?.items.reduce((sum, i) => sum + i.requested_quantity, 0)} units
              </p>
            </div>

            <div>
              <Label>Supplier</Label>
              <Input
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder="Enter supplier name"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
              <Button onClick={convertToPO} className="bg-blue-600 hover:bg-blue-700 text-white">
                Create Purchase Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseRequisitions;
